/**
 * ShipmentModal.jsx
 * ─────────────────
 * Full-screen modal for starting a new BOL shipment session.
 * Fires when operator taps their first tool with no active BOL.
 * Truck type selector + fields vary per type.
 */

import React, { useState } from 'react';
import './ShipmentModal.css';

const TRUCK_TYPES = ['FLATBED', 'ROLL-OFF', 'OTHER'];

const DEFAULTS = {
  FLATBED:  { advisoryWeightLbs: '48000' },
  'ROLL-OFF': { advisoryWeightLbs: '20000' },
  OTHER:    { advisoryWeightLbs: '' },
};

export default function ShipmentModal({ isOpen, onStart, onCancel, projectNumber, defaultTruckType = 'FLATBED' }) {
  const [truckType, setTruckType] = useState(defaultTruckType);
  const [haulerCompany, setHaulerCompany] = useState('');
  const [truckNumber, setTruckNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [advisoryWeightLbs, setAdvisoryWeightLbs] = useState(
    DEFAULTS[defaultTruckType]?.advisoryWeightLbs || '48000'
  );

  function handleTypeChange(type) {
    setTruckType(type);
    setAdvisoryWeightLbs(DEFAULTS[type]?.advisoryWeightLbs || '');
    if (type === 'ROLL-OFF') setDriverName('');
  }

  function isValid() {
    if (!haulerCompany.trim()) return false;
    if (!truckNumber.trim()) return false;
    if (truckType !== 'ROLL-OFF' && !driverName.trim()) return false;
    if (!advisoryWeightLbs || Number(advisoryWeightLbs) <= 0) return false;
    return true;
  }

  function handleStart() {
    if (!isValid()) return;
    onStart({
      truckType,
      haulerCompany: haulerCompany.trim(),
      truckNumber: truckNumber.trim(),
      driverName: truckType === 'ROLL-OFF' ? '' : driverName.trim(),
      advisoryWeightLbs: Number(advisoryWeightLbs),
    });
  }

  if (!isOpen) return null;

  const truckLabel = truckType === 'ROLL-OFF' ? 'Container Number' : 'Truck Number';

  return (
    <div className="sm-overlay">
      <div className="sm-card">
        <div className="sm-header">
          <span className="sm-header-title">New Shipment</span>
          {projectNumber && <span className="sm-header-project">{projectNumber}</span>}
          {onCancel && (
            <button className="sm-close-btn" onClick={onCancel}>&times;</button>
          )}
        </div>

        {/* ── Truck type selector ──────────────────────────────────────── */}
        <div className="sm-type-row">
          {TRUCK_TYPES.map(type => (
            <button
              key={type}
              className={`sm-type-btn ${truckType === type ? 'active' : ''}`}
              onClick={() => handleTypeChange(type)}
            >
              {type}
            </button>
          ))}
        </div>

        {/* ── Fields ──────────────────────────────────────────────────── */}
        <div className="sm-fields">
          <label className="sm-field">
            <span className="sm-field-label">Hauler Company *</span>
            <input type="text" className="sm-input" value={haulerCompany}
              onChange={e => setHaulerCompany(e.target.value)} placeholder="Company name" />
          </label>

          <label className="sm-field">
            <span className="sm-field-label">{truckLabel} *</span>
            <input type="text" className="sm-input" value={truckNumber}
              onChange={e => setTruckNumber(e.target.value)} placeholder={truckLabel} />
          </label>

          {truckType !== 'ROLL-OFF' && (
            <label className="sm-field">
              <span className="sm-field-label">Driver Name *</span>
              <input type="text" className="sm-input" value={driverName}
                onChange={e => setDriverName(e.target.value)} placeholder="Driver full name" />
            </label>
          )}

          <label className="sm-field">
            <span className="sm-field-label">Advisory Weight (lbs) *</span>
            <input type="number" className="sm-input" value={advisoryWeightLbs}
              onChange={e => setAdvisoryWeightLbs(e.target.value)}
              placeholder="Weight limit" />
          </label>
        </div>

        {/* ── Start button ────────────────────────────────────────────── */}
        <button
          className="sm-start-btn"
          disabled={!isValid()}
          onClick={handleStart}
        >
          Start Loading
        </button>
      </div>
    </div>
  );
}
